<script setup lang="ts">
import Button from '@/Components/Ui/Button.vue';
import Card from '@/Components/Ui/Card.vue';
import Checkbox from '@/Components/Ui/Checkbox.vue';
import Input from '@/Components/Ui/Input.vue';
import PublicLayout from '@/Layouts/PublicLayout.vue';
import { login, register } from '@/routes';
import {
    authenticationOptions,
    login as loginPasskeys,
} from '@/routes/passkeys';
import { request as passwordRequest } from '@/routes/password';
import { firstErrorMessage, useMutation } from '@/composables/useMutation';
import { Head, Link, useForm, usePage } from '@inertiajs/vue3';
import {
    browserSupportsWebAuthn,
    startAuthentication,
} from '@simplewebauthn/browser';
import axios from 'axios';
import { trans } from 'laravel-vue-i18n';
import { Key } from 'lucide-vue-next';
import { computed, ref } from 'vue';

defineProps<{
    canResetPassword?: boolean;
    status?: string;
}>();
const page = usePage();
const errorPasskey = ref((page.props.errors?.passkey as string) ?? '');

const form = useForm({
    email: '',
    password: '',
    remember: false,
});

const isPreparingPasskey = ref(false);
const { processing: isSendingPasskey, post: postPasskey } = useMutation();
const isAuthenticatingWithPasskey = computed(
    () => isPreparingPasskey.value || isSendingPasskey.value,
);

const submit = () => {
    form.post(login().url, {
        onFinish: () => {
            form.reset('password');
        },
    });
};

const authenticateWithPasskey = async () => {
    if (isAuthenticatingWithPasskey.value) {
        return;
    }

    if (!browserSupportsWebAuthn()) {
        errorPasskey.value = trans('profile.passkeys.browser_not_supported');
        return;
    }

    errorPasskey.value = '';

    isPreparingPasskey.value = true;

    try {
        const response = await axios.get(authenticationOptions().url);
        const options = response.data;

        const startAuthenticationResponse = await startAuthentication({
            optionsJSON: options,
        });

        postPasskey(
            loginPasskeys.url(),
            {
                credential: startAuthenticationResponse as any,
            },
            {
                // Erreur affichée sous le bouton passkey.
                onError: (errors) => {
                    errorPasskey.value = firstErrorMessage(errors) ?? '';
                    return false;
                },
            },
        );
    } catch (error) {
        errorPasskey.value = trans(
            error instanceof Error && error.name === 'NotAllowedError'
                ? 'profile.passkeys.cancelled'
                : axios.isAxiosError(error) && !error.response
                  ? 'common.errors.network'
                  : 'profile.passkeys.error_something_went_wrong_authenticating_with_passkey',
        );
    } finally {
        isPreparingPasskey.value = false;
    }
};
</script>

<template>
    <PublicLayout>
        <Head :title="$t('auth.login.title')" />

        <Card class="w-full max-w-md" padding="lg">
            <h1
                class="font-display text-ink mb-6 text-2xl font-semibold tracking-[-0.01em]"
            >
                {{ $t('auth.login.title') }}
            </h1>

            <div v-if="status" class="text-positive mb-4 text-sm font-medium">
                {{ status }}
            </div>

            <form @submit.prevent="submit" class="space-y-4">
                <Input
                    v-model="form.email"
                    :label="$t('auth.fields.email')"
                    type="email"
                    :error="form.errors.email"
                    required
                    autofocus
                    autocomplete="username"
                />

                <Input
                    v-model="form.password"
                    :label="$t('auth.fields.password')"
                    type="password"
                    :error="form.errors.password"
                    required
                    autocomplete="current-password"
                />

                <div class="pt-1">
                    <Checkbox
                        v-model="form.remember"
                        :label="$t('auth.login.remember_me')"
                    />
                </div>

                <div class="flex items-center justify-end pt-2">
                    <Link
                        v-if="canResetPassword"
                        :href="passwordRequest().url"
                        class="text-text-soft hover:text-ink text-sm underline"
                    >
                        {{ $t('auth.login.forgot_password') }}
                    </Link>

                    <Button
                        type="submit"
                        class="ms-4"
                        :disabled="form.processing"
                        :loading="form.processing"
                    >
                        {{ $t('auth.login.button') }}
                    </Button>
                </div>

                <div class="relative pt-4">
                    <div
                        class="absolute inset-0 flex items-center"
                        aria-hidden="true"
                    >
                        <div class="border-border w-full border-t" />
                    </div>
                    <div class="relative flex justify-center text-sm">
                        <span class="bg-surface text-text-soft px-2">
                            {{ $t('common.or') }}
                        </span>
                    </div>
                </div>

                <div class="pt-2">
                    <Button
                        variant="secondary"
                        type="button"
                        :disabled="isAuthenticatingWithPasskey"
                        :loading="isAuthenticatingWithPasskey"
                        class="w-full justify-center"
                        @click="authenticateWithPasskey"
                    >
                        <Key class="mr-2 h-4 w-4" />
                        {{ $t('auth.login.with_passkey') }}
                    </Button>
                    <p
                        v-if="errorPasskey"
                        class="text-negative mt-2 text-center text-xs"
                        role="alert"
                    >
                        {{ errorPasskey }}
                    </p>
                </div>

                <div class="pt-4 text-center">
                    <p class="text-text-soft text-sm">
                        {{ $t('auth.register.not_registered') }}
                        <Link
                            :href="register().url"
                            class="text-terracotta hover:text-terracotta-hover font-medium underline"
                        >
                            {{ $t('auth.register.create_account') }}
                        </Link>
                    </p>
                </div>
            </form>
        </Card>
    </PublicLayout>
</template>
